import React from "react";
import { Link } from "react-router-dom";
import { CirclePause, CirclePlay } from "lucide-react";

const PhoneCarousel = () => {
  const slides = [
    { src: "images/1.png", alt: "Ebbinghaus app screen preview 1" },
    { src: "images/2.png", alt: "Ebbinghaus app screen preview 2" },
    { src: "images/3.png", alt: "Ebbinghaus app screen preview 3" },
    { src: "images/4.png", alt: "Ebbinghaus app screen preview 4" },
    { src: "images/5.png", alt: "Ebbinghaus app screen preview 5" },
  ];

  const SLIDE_DURATION = 3000; // ms
  const RADIUS = 7;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [progress, setProgress] = React.useState(0); // 0 → 1
  const [isPaused, setIsPaused] = React.useState(false);
  const elapsedRef = React.useRef(0);

  // Auto-advance + circular progress
  React.useEffect(() => {
    if (isPaused) return;

    const start = Date.now() - elapsedRef.current;

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      elapsedRef.current = elapsed;

      const fraction = Math.min(elapsed / SLIDE_DURATION, 1);
      setProgress(fraction);

      if (fraction >= 1) {
        clearInterval(interval);
        elapsedRef.current = 0;
        setProgress(0);
        setCurrentIndex((prev) => (prev + 1) % slides.length);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused, slides.length]);

  const handleDotClick = (index) => {
    elapsedRef.current = 0;
    setProgress(0);
    setCurrentIndex(index);
  };

  const handleTogglePause = () => {
    setIsPaused((prev) => !prev);
  };

  const currentSlide = slides[currentIndex];

  return (
    <div className="relative flex flex-col items-center">
      <picture>
        <source srcSet={currentSlide.src} type="image/png" />
        <img
          src={currentSlide.src}
          alt={currentSlide.alt}
          className="h-[55vh] sm:h-[60vh] lg:h-[70vh] max-h-[720px] w-auto z-10 rounded-2xl lg:rounded-3xl border shadow-lg border-white/10"
        />
      </picture>

      <div className="mt-3 w-full flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={handleTogglePause}
          aria-pressed={isPaused}
        >
          {isPaused ? (
            <CirclePlay
              size={22}
              color="rgba(248,250,252,0.9)"
              aria-hidden="true"
              strokeWidth={1.6}
              className="shrink-0 transition-transform duration-200 bg-white/10 rounded-full p-[2px] backdrop-blur"
            />
          ) : (
            <CirclePause
              size={22}
              color="rgba(248,250,252,0.9)"
              aria-hidden="true"
              strokeWidth={1.6}
              className="shrink-0 transition-transform duration-200 bg-white/10 rounded-full p-[2px] backdrop-blur"
            />
          )}
        </button>

        <div className="flex justify-end flex-1 gap-2">
          {slides.map((_, index) => {
            const isActive = index === currentIndex;

            return (
              <button
                key={index}
                type="button"
                onClick={() => handleDotClick(index)}
                className="relative h-5 w-5 flex items-center justify-center"
                aria-label={`Go to slide ${index + 1}`}
              >
                <span className="absolute h-4 w-4 rounded-full bg-white/30 border border-white/40" />
                <svg
                  viewBox="0 0 20 20"
                  className="absolute h-6 w-6 -rotate-90"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r={RADIUS}
                    fill="transparent"
                    stroke={!isActive ? "rgba(148,163,184,0)" : "rgba(248,250,252,0.95)"}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
                  />
                </svg>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#020617] via-[#111827] to-[#1e293b] text-white font-calistoga relative overflow-hidden">
      {/* soft glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-45 mix-blend-soft-light
               bg-[radial-gradient(circle_at_top,_#facc1540,_transparent_60%)]"
        aria-hidden="true"
      />

      {/* Top bar / logo (overlay, not affecting hero centering) */}
      <header className="absolute z-20 top-0 left-0 right-0">
        <div className="max-w-[1000px] mx-auto px-6 pt-8 flex items-center justify-between">
          <Link to="/">
            <span className="text-2xl font-medium tracking-wide">
              Ebbinghaus
            </span>
          </Link>
        </div>
      </header>

      {/* Hero – vertically centered in the viewport */}
      <main className="relative z-10 flex items-center justify-center min-h-screen mt-12 md:mt-0">
        <div className="max-w-[1000px] mx-auto px-6 py-10 lg:py-16 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 w-full">
          {/* Copy */}
          <div className="flex-1 space-y-6 text-center lg:text-left">
            <div className="font-calistoga text-5xl sm:text-5xl lg:text-6xl leading-[0.9] space-y-1">
              <span className="block">Beautifully</span>
              <span className="block">simple</span>
              <span className="block">flashcards</span>
            </div>

            <p className="text-base sm:text-lg font-nunito max-w-md mx-auto lg:mx-0 text-white/90">
              Ebbinghaus is a simple, beautiful flashcard app that follows the
              forgetting curve so you review the right words at the right time.
            </p>

            <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
              <div className="py-1 px-2 bg-[#fff]/20 font-nunito text-[#fff] text-sm font-semibold rounded-lg">
                100% free
              </div>
              <div className="py-1 px-2 bg-[#fff]/20 font-nunito text-[#fff] text-sm font-semibold rounded-lg">
                No ads
              </div>
              <div className="py-1 px-2 bg-[#fff]/20 font-nunito text-[#fff] text-sm font-semibold rounded-lg">
                No trackers
              </div>
              <div className="py-1 px-2 bg-[#fff]/20 font-nunito text-[#fff] text-sm font-semibold rounded-lg">
                No internet connection required
              </div>
            </div>

            <a
              href="https://apps.apple.com/us/app/ebbinghaus/id6450792114"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img
                src="images/appStore.svg"
                alt="Download on the App Store"
                className="h-12"
              />
            </a>
          </div>

          {/* Phone carousel (replacing video) */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <PhoneCarousel />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-black/60 text-white text-center text-sm py-4 mt-auto">
        <a
          href="https://josephmalam.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="px-2 font-nunito text-sm font-semibold "
        >
          &copy;{" "}
          <span className="underline decoration-white/50">Joseph Malam</span>{" "}
          2022-2025
        </a>
        <div className="flex flex-row items-center justify-center pt-2 font-nunito text-xs">
          <Link to="/support" className="px-2 underline decoration-white/50">
            Support
          </Link>
          &bull;
          <Link to="/privacy" className="px-2 underline decoration-white/50">
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Home;
